import { addDays, format, parseISO } from "date-fns";
import type { Language } from "@/lib/i18n/types";

/** BCP-47 tag per UI language. Estonian groups with a comma and trails the €. */
const MONEY_LOCALE: Record<Language, string> = { en: "en-EE", et: "et-EE" };

export function formatBillingDate(value: string) {
  return format(parseISO(value), "dd.MM.yyyy");
}

export function formatBillingMonth(value: string) {
  return format(parseISO(value), "MMMM yyyy");
}

export function formatCompactBillingPeriod(start: string, end: string) {
  const first = parseISO(start);
  const last = parseISO(end);
  const sameYear = first.getFullYear() === last.getFullYear();
  return `${format(first, sameYear ? "dd.MM" : "dd.MM.yyyy")}–${formatBillingDate(end)}`;
}

export function formatBillingMonthlyPrice(cents: number, language: Language) {
  return new Intl.NumberFormat(MONEY_LOCALE[language], {
    style: "currency",
    currency: "EUR",
    minimumFractionDigits: cents % 100 === 0 ? 0 : 2,
    maximumFractionDigits: 2,
  }).format(cents / 100);
}

export function formatBillingPeriod(start: string, end: string) {
  return `${formatBillingDate(start)}–${formatBillingDate(end)}`;
}

/**
 * Today as a comparable `yyyy-MM-dd` in Europe/Tallinn — the billing calendar.
 *
 * Every date the backend produces comes from `private.billing_today()`, which is
 * a Tallinn calendar date. Deriving the comparison day from UTC instead makes
 * the browser a day behind between midnight and 03:00 EEST, so an invoice that
 * became overdue that morning would still render "Due" for those three hours.
 * `en-CA` is the locale whose short date IS ISO 8601.
 */
export function todayInTallinn(now: Date = new Date()) {
  return new Intl.DateTimeFormat("en-CA", { timeZone: "Europe/Tallinn" }).format(now);
}

/** The day after `periodEnd` — invoices are issued on the 1st, in advance. */
export function nextInvoiceDate(periodEnd: string) {
  return format(addDays(parseISO(periodEnd), 1), "dd.MM.yyyy");
}

export function formatBillingMoney(cents: number, currency = "EUR", language: Language = "en") {
  const amount = cents / 100;
  try {
    return new Intl.NumberFormat(MONEY_LOCALE[language] ?? MONEY_LOCALE.en, {
      style: "currency",
      currency,
      minimumFractionDigits: 2,
    }).format(amount);
  } catch {
    // Intl throws RangeError on any currency that is not a well-formed ISO 4217
    // code, and the RPC types it as a plain string. One bad value must degrade
    // to a readable amount, not take the whole billing page down.
    return `${amount.toFixed(2)} ${currency}`.trim();
  }
}

/**
 * Pick the singular or plural wording for `count`.
 *
 * Estonian takes the partitive singular for every count above one ("1 hoone",
 * "5 hoonet"), so the two forms are always separate strings — never a suffixed
 * English one.
 */
export function plural(one: string, other: string, count: number) {
  return count === 1 ? one : other;
}
